import { BRAND } from "constants/brand";
import { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

import { Form } from "react-bootstrap";

import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";
import { useSelector } from "react-redux";
import useOrder from "hooks/useOrder";
import useDispatchOrderForm from "hooks/useDispatchOrderForm";
import FieldHelp from "components/Ui/FieldHelp/FieldHelp";

/**
 * "طلب سريع" for an ordinary vendor — a restaurant asking for a driver to pick
 * something up and deliver it. Formerly labelled "اطلب سائقاً" and named
 * AddOrder; renamed so the three sibling modals are each named for what they
 * create, and relabelled to match the vendor app's sheet.
 *
 * Posts to POST api/order/dispatch with NO discriminator. The API resolves the
 * policy from the logged-in user, and a vendor without `isServiceVendor` lands
 * on the driver-request policy: born status="preparing", type="delivery",
 * userId left NULL, `total` stored as the order's subtotal.
 *
 * The sibling modal AddServiceVendorQuickOrder handles the other branch. Order.js
 * decides which one opens; a vendor only ever sees one of the two, which is why
 * both carry the same label.
 *
 * FIELDS: the area and the preparing time (both required), the order details,
 * the order price, the customer's phone and the delivery fee.
 *
 * THE PRICE AND THE PHONE ARE BACK (api#143, Saher 2026-09-16): «يرجعلي رقم
 * التلفون وسعر ويضيفها على النسبة المطاعم». The price is sent as `total`, which
 * the API stores as the order's subtotal. REQUIRED HERE, as on the tablet, but
 * not in the API: the tablets that predate the price field must keep calling
 * drivers.
 *
 * NO COMMISSION SINCE 2026-10-13. api#143 made each quick order of a restaurant
 * count in its statement, at the price × its percentage. The office took that
 * off, because the restaurants did not use the quick order. The order keeps
 * the price, and the statement does not count it.
 *
 * THE SENTENCE UNDER THE PRICE COMES FROM THE API (GET api/order/dispatch/form),
 * from the same rule that decides the billing. The tablet shows the same one
 * (talabatk/talabatek-restaurant-printing#17). This form shows its own neutral
 * sentence only while the answer loads, or when the read fails.
 *
 * The delivery fee stays because it is the number the DRIVER is settled against.
 * It is separate from the price.
 *
 * NO DOM-INDEX MAPPING. The old version read `form.current[0..6]` by position,
 * so inserting or reordering a single Form.Group silently shifted every field
 * after it with nothing to catch the mistake. Inputs are `name`d and read
 * through FormData; add fields freely.
 *
 * The delivery fee is optional and OVERRIDES the area's configured cost when
 * given. Left blank, the server prices the order from the area exactly as
 * before — which is what every existing client does.
 */
const AddVendorQuickOrder = (props) => {
    const form = useRef();
    const nodeRef = useRef();
    const adminInfo = useSelector((state) => state.auth.userData);
    const { loading, createQuickOrder } = useOrder();
    const { form: serverForm } = useDispatchOrderForm({
        userId: adminInfo?.id,
        enabled: props.show,
    });

    const submitHandler = async (e) => {
        e.preventDefault();

        // Named fields, not positions. Empty strings are dropped so an untouched
        // optional input is genuinely absent rather than "" — the API treats a
        // blank required field as missing, and a blank fee as "use the area".
        const raw = new FormData(form.current);
        const orderData = new FormData();
        for (const [key, value] of raw.entries()) {
            if (String(value).trim() !== "") orderData.append(key, value);
        }

        try {
            const response = await createQuickOrder(orderData);

            if (response?.order) {
                props.showMessage("success", "تمت الاضافه", "تمت إضافه الطلب بنجاح");
                props.close();
            } else {
                props.showMessage("error", "هناك خطأ", response?.message || "حدث خطأ غير متوقع");
            }
        } catch (err) {
            // createQuickOrder rethrows so the API's Arabic message survives;
            // the old hook returned the error and this toast showed the English
            // "Request failed with status code N" instead.
            props.showMessage(
                "error",
                "هناك خطأ",
                err?.response?.data?.message || "حدث خطأ غير متوقع",
            );
        }
    };

    return (
        <>
            <div className={`model ${props.show ? "model-show" : ""}`} onClick={props.close}>
                <CSSTransition
                    mountOnEnter
                    unmountOnExit
                    nodeRef={nodeRef}
                    in={props.show}
                    timeout={400}
                    classNames="show"
                >
                    <div className="addItem" ref={nodeRef} onClick={(e) => e.stopPropagation()}>
                        {loading ? (
                            <div className="loading-spinner center">
                                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                            </div>
                        ) : null}
                        <div className="head center">
                            <h5>طلب سريع</h5>
                            <FontAwesomeIcon icon={faClose} onClick={props.close} />
                        </div>
                        <Form className="form" ref={form} onSubmit={submitHandler}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    المنطقه<span>*</span>
                                    <FieldHelp>
                                        منطقة التوصيل. تحدد سعر التوصيل والسائقين الذين يصلهم الطلب.
                                        الخيارات هي مناطق التوصيل المعرفة لحسابك.
                                    </FieldHelp>
                                </Form.Label>
                                <Form.Select name="areaId" required>
                                    {adminInfo.areas?.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    وقت التحضير<span>*</span>
                                    <FieldHelp>
                                        بالدقائق. الوقت المتوقع حتى يصبح الطلب جاهزًا للاستلام.
                                        السائق يخطط وصوله عليه، لذلك هو مطلوب. اكتب 0 إذا كان جاهزًا
                                        الآن.
                                    </FieldHelp>
                                </Form.Label>
                                <Form.Control
                                    name="time"
                                    type="number"
                                    min={0}
                                    step={5}
                                    defaultValue={5}
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    تفاصيل الطلب (اختياري)
                                    <FieldHelp>
                                        ما الذي يجب أن يستلمه السائق، وأي ملاحظات أخرى. هذا هو ما
                                        يقرأه السائق.
                                    </FieldHelp>
                                </Form.Label>
                                <Form.Control name="notes" as="textarea" rows={2} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    سعر الطلب (₪)<span>*</span>
                                    <FieldHelp>
                                        {serverForm?.hints?.total ?? "قيمة الطلب بدون التوصيل."}
                                    </FieldHelp>
                                </Form.Label>
                                <Form.Control
                                    name="total"
                                    type="number"
                                    min={0}
                                    step="0.5"
                                    required
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    رقم الهاتف (اختياري)
                                    <FieldHelp>رقم الزبون، ليتصل به السائق عند التوصيل.</FieldHelp>
                                </Form.Label>
                                <Form.Control name="phone" type="tel" inputMode="tel" dir="ltr" />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    سعر التوصيل (₪، اختياري)
                                    <FieldHelp>
                                        اتركه فارغًا ليُحسب تلقائيًا من سعر المنطقة. إذا أدخلت قيمة
                                        فستُستخدم بدلًا منه.
                                    </FieldHelp>
                                </Form.Label>
                                <Form.Control name="shipping" type="number" min={0} step="0.5" />
                            </Form.Group>

                            <div className="buttons">
                                <button className="cancel" onClick={props.close} type="button">
                                    إلغاء
                                </button>
                                <button className="button" type="submit" disabled={loading}>
                                    إضافه
                                </button>
                            </div>
                        </Form>
                    </div>
                </CSSTransition>
            </div>
        </>
    );
};

export default AddVendorQuickOrder;
